import React, { useState } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const FeaturedStory = ({ story, onViewDetails }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showVideo, setShowVideo] = useState(false);

  const nextImage = () => {
    setCurrentImageIndex((prev) => 
      prev === story.images.length - 1 ? 0 : prev + 1
    );
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => 
      prev === 0 ? story.images.length - 1 : prev - 1
    );
  };

  return (
    <div className="bg-gradient-to-br from-primary/5 to-secondary/5 rounded-xl construction-shadow-lg overflow-hidden mb-12">
      <div className="grid lg:grid-cols-2 gap-0">
        {/* Image Gallery */}
        <div className="relative h-96 lg:h-auto">
          <div className="absolute top-4 left-4 z-10">
            <span className="px-4 py-2 bg-primary text-primary-foreground text-sm font-bold rounded-full flex items-center space-x-2">
              <Icon name="Star" size={16} className="fill-current" />
              <span>Featured Story</span>
            </span>
          </div>

          {/* Main Image */}
          <div className="relative h-full overflow-hidden">
            <Image
              src={story.images[currentImageIndex]}
              alt={`${story.title} - Image ${currentImageIndex + 1}`}
              className="w-full h-full object-cover"
            />
            
            {/* Navigation Arrows */}
            {story.images.length > 1 && (
              <>
                <button
                  onClick={prevImage}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center transition-construction"
                >
                  <Icon name="ChevronLeft" size={20} className="text-primary" />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center transition-construction"
                >
                  <Icon name="ChevronRight" size={20} className="text-primary" />
                </button>
              </>
            )}

            {/* Image Indicators */}
            {story.images.length > 1 && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2">
                {story.images.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`w-2 h-2 rounded-full transition-construction ${
                      index === currentImageIndex ? 'bg-white' : 'bg-white/50'
                    }`}
                  />
                ))}
              </div>
            )}

            {/* Video Play Button */}
            {story.hasVideo && (
              <button
                onClick={() => setShowVideo(true)}
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 bg-primary/90 hover:bg-primary rounded-full flex items-center justify-center transition-construction group"
              >
                <Icon name="Play" size={24} className="text-primary-foreground ml-1 group-hover:scale-110 transition-construction" />
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="p-8 lg:p-12 flex flex-col justify-center">
          <div className="flex items-center space-x-4 mb-4">
            <span className="px-3 py-1 bg-secondary text-secondary-foreground text-sm font-medium rounded-full">
              {story.projectType}
            </span>
            <div className="flex items-center space-x-1 text-warning">
              {[...Array(5)].map((_, i) => (
                <Icon
                  key={i}
                  name="Star"
                  size={16}
                  className={i < story.rating ? 'fill-current' : 'opacity-30'}
                />
              ))}
              <span className="ml-2 text-sm text-muted-foreground">({story.rating}/5)</span>
            </div>
          </div>

          <h1 className="font-headline font-bold text-3xl lg:text-4xl text-card-foreground mb-4">
            {story.title}
          </h1>

          <p className="text-muted-foreground text-lg mb-6 leading-relaxed">
            {story.description}
          </p>

          {/* Key Metrics */}
          <div className="grid grid-cols-2 gap-6 mb-8">
            <div className="text-center p-4 bg-success/10 rounded-lg">
              <div className="flex items-center justify-center space-x-2 mb-2">
                <Icon name="Clock" size={20} className="text-success" />
                <span className="text-2xl font-bold text-success">{story.timeReduction}</span>
              </div>
              <p className="text-sm text-muted-foreground">Time Saved</p>
            </div>
            <div className="text-center p-4 bg-primary/10 rounded-lg">
              <div className="flex items-center justify-center space-x-2 mb-2">
                <Icon name="TrendingDown" size={20} className="text-primary" />
                <span className="text-2xl font-bold text-primary">₹{story.costSavings}</span>
              </div>
              <p className="text-sm text-muted-foreground">Cost Savings</p>
            </div>
          </div>

          {/* Client Info */}
          <div className="flex items-center space-x-4 mb-8">
            <Image
              src={story.client.avatar}
              alt={story.client.name}
              className="w-16 h-16 rounded-full object-cover"
            />
            <div>
              <p className="font-bold text-lg text-card-foreground">{story.client.name}</p>
              <p className="text-muted-foreground">{story.client.title}</p>
              <p className="text-sm text-muted-foreground flex items-center space-x-1">
                <Icon name="MapPin" size={14} />
                <span>{story.client.location}</span>
              </p>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              variant="default"
              size="lg"
              iconName="BookOpen"
              iconPosition="left"
              onClick={() => onViewDetails(story)}
              className="flex-1"
            >
              Read Full Case Study
            </Button>
            <Button
              variant="outline"
              size="lg"
              iconName="Share2"
              iconPosition="left"
              className="flex-1"
            >
              Share Story
            </Button>
          </div>
        </div>
      </div>

      {/* Video Modal */}
      {showVideo && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="relative w-full max-w-4xl aspect-video bg-black rounded-lg overflow-hidden">
            <button
              onClick={() => setShowVideo(false)}
              className="absolute top-4 right-4 z-10 w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-construction"
            >
              <Icon name="X" size={20} className="text-white" />
            </button>
            <iframe
              src={story.videoUrl}
              title={`${story.title} - Video Testimonial`}
              className="w-full h-full"
              allowFullScreen
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default FeaturedStory;